/** @jsxImportSource @emotion/react */
import { SuitabilityBadge } from '@app/domains/suitability/components/SuitabilityBadge'
import { HazardLayerMetadata } from '@app/types/layerTypes'
import { Box, Button, Chip, Paper, Typography } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import {
  badgesWrapStyles,
  buttonStyles,
  cardStyles,
  descriptionStyles,
  headerStyles,
  neutralBadgeStyles,
  titleStyles,
  typeChipStyles,
  warningBadgeStyles,
} from './HazardReferenceCard.styles'
import { SimpleMarkdownRenderer } from './SimpleMarkdownRenderer'

type HazardReferenceCardProps = {
  layer: HazardLayerMetadata
  activeSlug: string | null
  isPinned: boolean
  isDisabled: boolean
  onTogglePin: (layer: HazardLayerMetadata) => void
  onDragStart: (layer: HazardLayerMetadata) => void
}

export const HazardReferenceCard = ({
  layer,
  activeSlug,
  isPinned,
  isDisabled,
  onTogglePin,
  onDragStart,
}: HazardReferenceCardProps) => {
  const theme = useTheme()
  const meta = layer.metadata

  const activeSuitability = activeSlug
    ? (layer.suitability.find((s) => s.use_case_slug === activeSlug) ?? null)
    : null

  return (
    <Paper
      draggable
      onDragStart={(e) => {
        e.dataTransfer.effectAllowed = 'copy'
        onDragStart(layer)
      }}
      css={cardStyles(theme, isPinned)}
      sx={{ cursor: 'grab', '&:active': { cursor: 'grabbing' } }}
    >
      <Box css={headerStyles}>
        <Typography css={titleStyles(theme)}>{meta?.title ?? layer.id}</Typography>

        <Chip label={layer.type} size="small" css={typeChipStyles(theme)} />
      </Box>

      {meta?.description_md && (
        <Box css={descriptionStyles(theme)}>
          <SimpleMarkdownRenderer markdown={meta.description_md} />
        </Box>
      )}

      <Box css={badgesWrapStyles}>
        {activeSuitability && (
          <SuitabilityBadge
            letterGrade={activeSuitability.suitability_grade}
            label={`Suitability: ${activeSuitability.suitability_grade}`}
          />
        )}

        {meta?.confidence_rating && (
          <Chip
            label={`Confidence: ${meta.confidence_rating}`}
            size="small"
            css={neutralBadgeStyles(theme)}
          />
        )}

        {meta?.limitations_caveats_md && (
          <Chip label="Limitations noted" size="small" css={warningBadgeStyles(theme)} />
        )}
      </Box>

      <Button
        size="small"
        disableElevation
        disabled={!isPinned && isDisabled}
        onClick={() => onTogglePin(layer)}
        css={buttonStyles(theme, isPinned)}
      >
        {isPinned ? '✓ Pinned' : '+ Pin to compare'}
      </Button>
    </Paper>
  )
}
